import { getSubscriptionCreationCurrency } from '../../lib/subscription-creation-currency'
import * as React from 'react'
import { getAffiliateIdFromStorage as defaultGetAffiliateIdFromStorage } from '@repo/affiliate-id'
import { usePaymentsDialog } from '../../components/payments-dialog-wrapper-context'
import { useIsLoggedInQuery } from 'auth/hooks'
import { userEligibleToAutostartQueryFetcher } from '@repo/cf-user/gen/user'
import type { UserEligibleToAutostartQuery } from '@repo/cf-user/gen/user'
import { paymentsAutoStartFreeTrialMutationFetcher } from '../../gen/stripe'
import type { PaymentsAutoStartFreeTrialMutation } from '../../gen/stripe'
import * as Sentry from '@sentry/react'
import { SentryFilterTagAutostartFreeTrial } from '@repo/sentry-utils/constants'
import { sendAmplitudeEvent, AmplitudeEvents } from '@repo/analytics/amplitude/utils'
import { mapCurrencyToAvailableCurrencyCode } from '@repo/cf-user/hooks/use-user-data'
import { SubscriptionTypeAutoStartTrial } from 'codegen/types'
import { readPaymentModalEntryPoint } from '../../payments-dialog/analytics'

type SubscriptionType = 'all-access' | 'studio-ai'

export interface StartFreeTrialProps {
  redirectUrl?: string
  openInNewTab?: boolean
  subscriptionType?: SubscriptionType
  promoCode?: string
  // Injected so tests can supply a fake without module mocks; production uses
  // the real localStorage-backed reader from `@repo/affiliate-id`.
  getAffiliateIdFromStorage?: () => string | null
}

type AutostartUser = NonNullable<NonNullable<UserEligibleToAutostartQuery['me']>['user']>
type EligibleAutostartUser = AutostartUser & {
  stripeCustomer: NonNullable<AutostartUser['stripeCustomer']>
}
type AutostartResponse = PaymentsAutoStartFreeTrialMutation['paymentsAutoStartTrial']
interface AutostartEventProperties extends Record<string, unknown> {
  subscription_type: SubscriptionType
  entry_point: ReturnType<typeof readPaymentModalEntryPoint>
  promoCode: string
}
interface StartFreeTrialOptions {
  subscriptionType?: SubscriptionType
  onSuccess?: () => void
  onError?: () => void
  promoCode?: string
  getAffiliateIdFromStorage?: () => string | null
}

let autoStartFreeTrialAttempted = false

function reportAutostartError(message: string) {
  Sentry.captureException(new Error(message), {
    tags: { component: SentryFilterTagAutostartFreeTrial },
  })
}

async function fetchAutostartUser() {
  try {
    const userData = await userEligibleToAutostartQueryFetcher()()
    return userData.me?.user
  } catch (error) {
    reportAutostartError(
      `useUserEligibleToAutostartQuery fetcher failed with error: ${(error as Error)?.message || error}`,
    )
    return null
  }
}

function canAutostartFreeTrial(
  user: AutostartUser | null | undefined,
  subscriptionType: SubscriptionType,
): user is EligibleAutostartUser {
  if (!user?.stripeCustomer?.id) {
    return false
  }

  const access =
    subscriptionType === 'all-access'
      ? user.subscriptionAccess.allAccess
      : user.subscriptionAccess.studioAi
  return Boolean(access.isEligibleForAutoStartTrial)
}

function reportAutostartMutationFailure({
  errors,
  stripeSubscriptionId,
  success,
}: AutostartResponse) {
  if (!stripeSubscriptionId) {
    reportAutostartError(
      `paymentsAutoStartFreeTrial mutation did not return a stripe subscription id: ${errors?.join(', ')}`,
    )
    return
  }
  reportAutostartError(
    `paymentsAutoStartFreeTrial mutation failed with message: status: ${success}, errors: ${errors?.join(', ')}`,
  )
}

function getAutostartSuccessParams({
  user,
  subscriptionType,
  subscriptionId,
}: {
  user: EligibleAutostartUser
  subscriptionType: SubscriptionType
  subscriptionId: string
}) {
  return new URLSearchParams({
    is_free_trial_autostart_success: 'true',
    cf_user_id: user.id,
    cf_currency: String(user.currency.code),
    cf_subscription_type:
      subscriptionType === 'all-access' ? 'allAccessYearlyFreeTrial' : 'studioAiYearlyFreeTrial',
    cf_subscription_id: subscriptionId,
    cf_stripe_account: user.stripeCustomer.account,
  })
}

function scheduleAutostartSuccessRedirect(params: URLSearchParams) {
  // Let analytics and state updates finish before navigating away.
  setTimeout(() => {
    const redirectUrl = new URL(window.location.href)
    redirectUrl.hash = ''
    for (const [key, value] of params) {
      redirectUrl.searchParams.set(key, value)
    }
    window.location.href = redirectUrl.toString()
  }, 500)
}

async function submitAutostartFreeTrial({
  user,
  subscriptionType,
  promoCode,
  getAffiliateIdFromStorage,
  onSuccess,
  eventProperties,
}: {
  user: EligibleAutostartUser
  subscriptionType: SubscriptionType
  promoCode?: string
  getAffiliateIdFromStorage: () => string | null
  onSuccess?: () => void
  eventProperties: AutostartEventProperties
}) {
  sendAmplitudeEvent(AmplitudeEvents.SUBSCRIPTION_AUTO_START_CLICKED, eventProperties)
  try {
    const result = await paymentsAutoStartFreeTrialMutationFetcher({
      stripeCustomerId: user.stripeCustomerId,
      currencyCode: mapCurrencyToAvailableCurrencyCode(
        getSubscriptionCreationCurrency(user) ?? user.currency.code,
      ),
      promoCode,
      subscriptionType:
        subscriptionType === 'all-access'
          ? SubscriptionTypeAutoStartTrial.AllAccessYearly
          : SubscriptionTypeAutoStartTrial.StudioAiYearlyV2,
      // Forward the captured ref for apps that cannot share the affwp_ref cookie
      // cross-domain. Cookie-bearing domains are handled by BE.
      affiliateId: getAffiliateIdFromStorage() ?? undefined,
    })
    const autostartResponse = result.paymentsAutoStartTrial
    if (autostartResponse.success && autostartResponse.stripeSubscriptionId) {
      sendAmplitudeEvent(AmplitudeEvents.SUBSCRIPTION_AUTO_STARTED_SUCCESS, eventProperties)
      onSuccess?.()
      return getAutostartSuccessParams({
        user,
        subscriptionType,
        subscriptionId: autostartResponse.stripeSubscriptionId,
      })
    }

    reportAutostartMutationFailure(autostartResponse)
  } catch (error) {
    reportAutostartError(
      `paymentsAutoStartFreeTrial mutation failed with error: ${(error as Error)?.message || error}`,
    )
  }
  return null
}

export async function startFreeTrial({
  onSuccess,
  onError,
  subscriptionType = 'all-access',
  promoCode,
  getAffiliateIdFromStorage = defaultGetAffiliateIdFromStorage,
}: StartFreeTrialOptions) {
  if (autoStartFreeTrialAttempted) {
    return
  }

  autoStartFreeTrialAttempted = true
  const user = await fetchAutostartUser()
  const eventProperties: AutostartEventProperties = {
    subscription_type: subscriptionType,
    entry_point: readPaymentModalEntryPoint(),
    promoCode: promoCode || 'none',
  }
  if (canAutostartFreeTrial(user, subscriptionType)) {
    const redirectParams = await submitAutostartFreeTrial({
      user,
      subscriptionType,
      promoCode,
      getAffiliateIdFromStorage,
      onSuccess,
      eventProperties,
    })
    if (redirectParams) {
      autoStartFreeTrialAttempted = false
      scheduleAutostartSuccessRedirect(redirectParams)
      return
    }
  }

  sendAmplitudeEvent(AmplitudeEvents.SUBSCRIPTION_AUTO_STARTED_FAILED, eventProperties)
  onError?.()
  autoStartFreeTrialAttempted = false
}

function redirectLoggedOutUser({
  subscriptionType,
  redirectUrl,
  openInNewTab,
}: Pick<StartFreeTrialProps, 'subscriptionType' | 'redirectUrl' | 'openInNewTab'>) {
  const target =
    redirectUrl ||
    (subscriptionType === 'all-access' ? '/b/free-trial/' : '/b/free-trial-aistudio/')
  if (openInNewTab) {
    window.open(target, '_blank')
  } else {
    window.location.href = target
  }
}

function openFreeTrialCheckout(
  defaultPlan: 'freeTrialYearly' | 'aiStudio',
  open: ReturnType<typeof usePaymentsDialog>['open'],
) {
  if (window.cfOpenPaymentsDialog) {
    window.cfOpenPaymentsDialog({ defaultPlan })
  } else {
    open(defaultPlan)
  }
}

export function useStartFreeTrial({
  subscriptionType = 'all-access',
  redirectUrl,
  openInNewTab = false,
  promoCode,
  getAffiliateIdFromStorage = defaultGetAffiliateIdFromStorage,
}: StartFreeTrialProps = {}) {
  const [isPending, setIsPending] = React.useState(false)
  const { open } = usePaymentsDialog()
  const { data: isUserLoggedIn } = useIsLoggedInQuery()

  const defaultPlan = subscriptionType === 'all-access' ? 'freeTrialYearly' : 'aiStudio'

  const handleStartFreeTrial = async () => {
    if (isPending) {
      return
    }
    if (!isUserLoggedIn) {
      redirectLoggedOutUser({ subscriptionType, redirectUrl, openInNewTab })
      return
    }

    setIsPending(true)
    await startFreeTrial({
      subscriptionType,
      promoCode,
      getAffiliateIdFromStorage,
      onSuccess: () => {
        setIsPending(false)
      },
      onError: () => {
        setIsPending(false)
        openFreeTrialCheckout(defaultPlan, open)
      },
    })
  }

  return { startFreeTrial: handleStartFreeTrial, isPending }
}
